import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  useApproveAssignment,
  useBroker,
  useBrokerAssignments,
  useBrokerRates,
  useCurrentBrokerRates,
  useDenyAssignment,
  useRemoveDriverFromBroker,
  useUpdateBrokerStatus,
} from '@/hooks/useBrokers';
import { useCredentialTypes } from '@/hooks/useCredentialTypes';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cardVariants } from '@/lib/design-system';
import { cn } from '@/lib/utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { EnhancedDataView } from '@/components/ui/enhanced-data-view';
import { DetailPageHeader } from '@/components/ui/DetailPageHeader';
import {
  EnhancedTable,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Building2,
  CheckCircle,
  MoreVertical,
  Users,
  FileCheck,
  MapPin,
  XCircle,
  Trash2,
  Pencil,
  Lock,
  UserPlus,
  Zap,
  Eye,
  Calendar,
  Briefcase as BriefcaseIcon,
} from 'lucide-react';
import { UnifiedAssignmentModal } from '@/components/features/shared/assignment';
import BrokerFormModal from '@/components/features/admin/BrokerFormModal';
import type { BrokerContext } from '@/components/features/shared/assignment';
import type { AssignmentStatus, BrokerStatus, DriverBrokerAssignment, VehicleType, BrokerAssignmentMode, TripSourceType } from '@/types/broker';
import { getBrokerAssignmentMode, getAssignmentModeLabel, getSourceTypeLabel, SOURCE_TYPE_CONFIG } from '@/types/broker';
import { Hospital, Shield, User, Briefcase } from 'lucide-react';

// DS-compliant: Use Badge variants instead of custom color classes
const statusVariant: Record<BrokerStatus, 'default' | 'secondary'> = {
  active: 'default',
  inactive: 'secondary',
};

const assignmentStatusVariant: Record<AssignmentStatus, 'default' | 'secondary' | 'outline'> = {
  assigned: 'default',
  pending: 'secondary',
  removed: 'outline',
};

// DS-compliant: Use semantic colors from the design system
const assignmentModeConfig: Record<BrokerAssignmentMode, { icon: React.ReactNode; className: string }> = {
  admin_only: {
    icon: <Lock className="w-4 h-4" />,
    className: 'text-muted-foreground',
  },
  driver_requests: {
    icon: <UserPlus className="w-4 h-4" />,
    className: 'text-primary',  // DS semantic color
  },
  driver_auto_signup: {
    icon: <Zap className="w-4 h-4" />,
    className: 'text-success',  // DS semantic color
  },
};

const sourceTypeIcons: Record<TripSourceType, React.ReactNode> = {
  state_broker: <Building2 className="w-4 h-4" />,
  facility: <Hospital className="w-4 h-4" />,
  insurance: <Shield className="w-4 h-4" />,
  private: <User className="w-4 h-4" />,
  corporate: <Briefcase className="w-4 h-4" />,
};

const vehicleTypeLabels: Record<VehicleType, string> = {
  sedan: 'Sedan',
  suv: 'SUV',
  minivan: 'Minivan',
  wheelchair_van: 'Wheelchair Van',
  stretcher_van: 'Stretcher Van',
};

export default function BrokerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile, isAdmin } = useAuth();
  const companyId = profile?.company_id;

  const { data: broker, isLoading, error } = useBroker(id || '');
  const { data: assignments, isLoading: assignmentsLoading } = useBrokerAssignments(id || undefined);
  const { data: currentRates, isLoading: ratesLoading } = useCurrentBrokerRates(id || undefined);
  const { data: rateHistory } = useBrokerRates(id || undefined);
  const { data: credentialTypes, isLoading: credentialsLoading } = useCredentialTypes(companyId ?? undefined);

  const updateStatus = useUpdateBrokerStatus();
  const approveAssignment = useApproveAssignment();
  const denyAssignment = useDenyAssignment();
  const removeAssignment = useRemoveDriverFromBroker();

  const [assignDriversOpen, setAssignDriversOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  // Drivers tab filters
  const [driverSearch, setDriverSearch] = useState('');
  const [driverStatusFilter, setDriverStatusFilter] = useState<AssignmentStatus | 'all'>('all');

  const brokerCredentials = useMemo(() => {
    return (credentialTypes || []).filter(
      (type) => type.scope === 'broker' && type.broker_id === broker?.id,
    );
  }, [credentialTypes, broker?.id]);

  const assignedCount = assignments?.filter((a) => a.status === 'assigned').length ?? 0;
  const pendingCount = assignments?.filter((a) => a.status === 'pending').length ?? 0;

  // Filtered assignments for the Drivers tab
  const filteredAssignments = useMemo(() => {
    return (assignments || []).filter((assignment) => {
      const matchesSearch =
        !driverSearch ||
        assignment.driver?.user?.full_name?.toLowerCase().includes(driverSearch.toLowerCase()) ||
        assignment.driver?.user?.email?.toLowerCase().includes(driverSearch.toLowerCase());
      const matchesStatus = driverStatusFilter === 'all' || assignment.status === driverStatusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [assignments, driverSearch, driverStatusFilter]);

  // Clear all filters handler for Drivers tab
  const handleClearDriverFilters = () => {
    setDriverSearch('');
    setDriverStatusFilter('all');
  };

  const handleStatusChange = (status: BrokerStatus) => {
    if (!broker) return;
    updateStatus.mutate({ id: broker.id, status });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !broker) {
    return (
      <div className="text-center py-12">
        <h2 className="text-lg font-semibold text-destructive">Trip source not found</h2>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/admin/brokers')}>
          Back to Trip Sources
        </Button>
      </div>
    );
  }

  const brokerContext: BrokerContext = {
    type: 'broker',
    broker,
  };

  // Build avatar/logo for header
  const avatar = (
    <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center shrink-0">
      {broker.logo_url ? (
        <img src={broker.logo_url} alt={broker.name} className="w-10 h-10 object-contain" />
      ) : (
        <Building2 className="w-6 h-6 text-muted-foreground" />
      )}
    </div>
  );

  // Build badges
  // Single status badge
  const badges = (
    <Badge variant={statusVariant[broker.status]}>
      {broker.status}
    </Badge>
  );

  // Build subtitle - includes broker code if present
  const subtitle = `${getSourceTypeLabel(broker.source_type)}${broker.code ? ` • ${broker.code}` : ''}${broker.contact_email ? ` • ${broker.contact_email}` : ''}`;

  // Build actions
  const actions = (
    <>
      <Button variant="outline" onClick={() => setEditOpen(true)}>
        <Pencil className="w-4 h-4 mr-2" />
        Edit
      </Button>
      <Button onClick={() => setAssignDriversOpen(true)}>Assign Drivers</Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" disabled={!isAdmin}>
            <MoreVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {broker.status === 'active' && (
            <DropdownMenuItem onClick={() => handleStatusChange('inactive')}>
              <XCircle className="w-4 h-4 mr-2" />
              Set Inactive
            </DropdownMenuItem>
          )}
          {broker.status === 'inactive' && (
            <DropdownMenuItem onClick={() => handleStatusChange('active')}>
              <CheckCircle className="w-4 h-4 mr-2" />
              Set Active
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setAssignDriversOpen(true)}>
            <Users className="w-4 h-4 mr-2" />
            Assign Drivers
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );

  // Tab list for header
  const tabsList = (
    <TabsList>
      <TabsTrigger value="overview">Overview</TabsTrigger>
      <TabsTrigger value="drivers">Drivers</TabsTrigger>
      <TabsTrigger value="credentials">Credentials</TabsTrigger>
      <TabsTrigger value="rates">Rates</TabsTrigger>
    </TabsList>
  );

  return (
    <div className="min-h-screen bg-background">
      <Tabs defaultValue="overview">
        {/* Full-width header with centered tabs */}
        <DetailPageHeader
          title={broker.name}
          subtitle={subtitle}
          badges={badges}
          avatar={avatar}
          onBack={() => navigate('/admin/brokers')}
          backLabel="Back to Trip Sources"
          centerContent={tabsList}
          actions={actions}
        />

        {/* Content area */}
        <div className="p-6">
          <div className="max-w-5xl mx-auto">

        <TabsContent value="overview" className="mt-0 space-y-6">
          {/* DS-compliant stat cards using cardVariants({ variant: "stats" }) */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card className={cn(cardVariants({ variant: 'stats', padding: 'none' }))}>
              <CardHeader>
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Driver Assignments
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <span className="text-2xl font-semibold">{assignedCount}</span>
                  <span className="text-sm text-muted-foreground">Assigned</span>
                </div>
                <div className="text-sm text-muted-foreground">{pendingCount} pending</div>
              </CardContent>
            </Card>

            <Card className={cn(cardVariants({ variant: 'stats', padding: 'none' }))}>
              <CardHeader>
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Required Credentials
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-2">
                  <FileCheck className="w-4 h-4 text-muted-foreground" />
                  <span className="text-2xl font-semibold">{brokerCredentials.length}</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => navigate('/admin/settings/credentials')}
                >
                  Manage Credentials
                </Button>
              </CardContent>
            </Card>

            <Card className={cn(cardVariants({ variant: 'stats', padding: 'none' }))}>
              <CardHeader>
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Service Area
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <span className="text-2xl font-semibold">{broker.service_states.length}</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {broker.service_states.length ? broker.service_states.join(', ') : '—'}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Assignment Mode Card */}
          {(() => {
            const mode = getBrokerAssignmentMode(broker);
            const config = assignmentModeConfig[mode];
            return (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Driver Assignment Mode
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={`flex items-center gap-2 ${config.className}`}>
                    {config.icon}
                    <span className="text-lg font-semibold">{getAssignmentModeLabel(mode)}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    {mode === 'admin_only' && 'Only admins can assign drivers to this trip source.'}
                    {mode === 'driver_requests' && 'Drivers can request to join. You will need to approve each request.'}
                    {mode === 'driver_auto_signup' && 'Drivers can join instantly. They still need credentials to be eligible for trips.'}
                  </p>
                </CardContent>
              </Card>
            );
          })()}

          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium">Contact & Address</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div>{broker.contact_name || '—'}</div>
                <div className="text-muted-foreground">{broker.contact_email || '—'}</div>
                <div className="text-muted-foreground">{broker.contact_phone || '—'}</div>
                <div className="text-muted-foreground">
                  {[broker.address_line1, broker.address_line2, broker.city, broker.state, broker.zip_code]
                    .filter(Boolean)
                    .join(', ') || '—'}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium">Requirements</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div>
                  <div className="text-muted-foreground">Vehicle Types</div>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {broker.accepted_vehicle_types.length ? (
                      broker.accepted_vehicle_types.map((type) => (
                        <Badge key={type} variant="secondary" className="capitalize">
                          {vehicleTypeLabels[type]}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">Employment Types</div>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {broker.accepted_employment_types.length ? (
                      broker.accepted_employment_types.map((type) => (
                        <Badge key={type} variant="secondary" className="uppercase">
                          {type}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="drivers" className="mt-0 space-y-4">
          <EnhancedDataView
            title="Driver Assignments"
            description="Manage drivers assigned to this trip source"
            defaultViewMode="table"
            searchValue={driverSearch}
            onSearchChange={setDriverSearch}
            searchPlaceholder="Search by name or email..."
            filters={[
              {
                value: driverStatusFilter,
                onValueChange: (v) => setDriverStatusFilter(v as AssignmentStatus | 'all'),
                label: 'Status',
                placeholder: 'All Status',
                options: [
                  { value: 'all', label: 'All Status' },
                  { value: 'assigned', label: 'Assigned' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'removed', label: 'Removed' },
                ],
              },
            ]}
            tableProps={{
              data: filteredAssignments,
              loading: assignmentsLoading,
              children: (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Driver</TableHead>
                      <TableHead>Employment Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Requested</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAssignments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="h-24 text-center">
                          <div className="flex flex-col items-center gap-2">
                            <Users className="w-8 h-8 text-muted-foreground" />
                            <p className="text-muted-foreground">
                              {driverSearch || driverStatusFilter !== 'all'
                                ? 'No matching assignments found'
                                : 'No drivers assigned yet'}
                            </p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredAssignments.map((assignment) => (
                        <DriverAssignmentRow
                          key={assignment.id}
                          assignment={assignment}
                          onApprove={() => approveAssignment.mutate(assignment.id)}
                          onDeny={() => denyAssignment.mutate({ assignmentId: assignment.id })}
                          onRemove={() => removeAssignment.mutate({ assignmentId: assignment.id })}
                          isApproving={approveAssignment.isPending}
                          isDenying={denyAssignment.isPending}
                          isRemoving={removeAssignment.isPending}
                          onView={() => navigate(`/admin/drivers/${assignment.driver_id}`)}
                        />
                      ))
                    )}
                  </TableBody>
                </Table>
              ),
            }}
            cardProps={{
              data: filteredAssignments,
              loading: assignmentsLoading,
              emptyState: (
                <Card className="p-12 text-center">
                  <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No drivers assigned</h3>
                  <p className="text-muted-foreground">
                    {driverSearch || driverStatusFilter !== 'all'
                      ? 'No matching assignments found'
                      : 'Assign drivers to this trip source to get started.'}
                  </p>
                </Card>
              ),
              renderCard: (assignment) => (
                <DriverAssignmentCard
                  key={assignment.id}
                  assignment={assignment}
                  onApprove={() => approveAssignment.mutate(assignment.id)}
                  onDeny={() => denyAssignment.mutate({ assignmentId: assignment.id })}
                  onRemove={() => removeAssignment.mutate({ assignmentId: assignment.id })}
                  isApproving={approveAssignment.isPending}
                  isDenying={denyAssignment.isPending}
                  isRemoving={removeAssignment.isPending}
                  onView={() => navigate(`/admin/drivers/${assignment.driver_id}`)}
                />
              ),
            }}
          />
        </TabsContent>

        <TabsContent value="credentials" className="mt-0 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium">Required Credentials</h3>
              <p className="text-sm text-muted-foreground">
                Credentials required specifically for this trip source.
              </p>
            </div>
            <Button variant="outline" onClick={() => navigate('/admin/settings/credentials')}>
              Manage Credential Types
            </Button>
          </div>

          {/* DS-compliant EnhancedTable with loading state */}
          <EnhancedTable
            loading={credentialsLoading}
            skeletonRows={3}
            skeletonColumns={3}
          >
            {brokerCredentials.length ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Credential Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Requirement</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {brokerCredentials.map((cred) => (
                    <TableRow key={cred.id}>
                      <TableCell className="font-medium">{cred.name}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {cred.description || '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {cred.requirement}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Card className="p-8 text-center">
                <FileCheck className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                <h4 className="font-medium">No credentials configured</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Create credential types scoped to this trip source.
                </p>
              </Card>
            )}
          </EnhancedTable>
        </TabsContent>

        <TabsContent value="rates" className="mt-0 space-y-6">
          <div>
            <h3 className="text-lg font-medium">Current Rates</h3>
            <p className="text-sm text-muted-foreground">
              Effective rates currently in use for this trip source.
            </p>
          </div>

          {/* DS-compliant EnhancedTable with loading state */}
          <EnhancedTable
            loading={ratesLoading}
            skeletonRows={3}
            skeletonColumns={4}
          >
            {currentRates?.length ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vehicle Type</TableHead>
                    <TableHead>Base Rate</TableHead>
                    <TableHead>Per Mile Rate</TableHead>
                    <TableHead>Effective From</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentRates.map((rate) => (
                    <TableRow key={rate.id}>
                      <TableCell className="font-medium">
                        {vehicleTypeLabels[rate.vehicle_type]}
                      </TableCell>
                      <TableCell>${rate.base_rate.toFixed(2)}</TableCell>
                      <TableCell>${rate.per_mile_rate.toFixed(2)}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {rate.effective_from}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Card className="p-8 text-center">
                <Building2 className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                <h4 className="font-medium">No current rates</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Add rates in the broker creation flow or update later.
                </p>
              </Card>
            )}
          </EnhancedTable>

          <div className="pt-4">
            <h4 className="text-md font-medium mb-3">Rate History</h4>
            {rateHistory?.length ? (
              <EnhancedTable>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Vehicle Type</TableHead>
                      <TableHead>Base Rate</TableHead>
                      <TableHead>Per Mile Rate</TableHead>
                      <TableHead>Effective From</TableHead>
                      <TableHead>Effective To</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rateHistory.map((rate) => (
                      <TableRow key={rate.id}>
                        <TableCell className="font-medium">
                          {vehicleTypeLabels[rate.vehicle_type]}
                        </TableCell>
                        <TableCell>${rate.base_rate.toFixed(2)}</TableCell>
                        <TableCell>${rate.per_mile_rate.toFixed(2)}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {rate.effective_from}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {rate.effective_to || 'Current'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </EnhancedTable>
            ) : (
              <p className="text-sm text-muted-foreground">No rate history available.</p>
            )}
          </div>
        </TabsContent>
          </div>
        </div>
      </Tabs>

      <UnifiedAssignmentModal
        open={assignDriversOpen}
        onOpenChange={setAssignDriversOpen}
        mode="assign-drivers-to-broker"
        context={brokerContext}
      />
      
      {profile?.company_id && (
        <BrokerFormModal
          companyId={profile.company_id}
          open={editOpen}
          onOpenChange={setEditOpen}
          broker={broker}
        />
      )}
    </div>
  );
}

// Extracted row component for driver assignments
function DriverAssignmentRow({
  assignment,
  onApprove,
  onDeny,
  onRemove,
  onView,
  isApproving,
  isDenying,
  isRemoving,
}: {
  assignment: DriverBrokerAssignment;
  onApprove: () => void;
  onDeny: () => void;
  onRemove: () => void;
  onView: () => void;
  isApproving: boolean;
  isDenying: boolean;
  isRemoving: boolean;
}) {
  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
            <span className="text-primary text-sm">
              {assignment.driver?.user?.full_name?.charAt(0).toUpperCase() || '?'}
            </span>
          </div>
          <div>
            <div className="font-medium">
              {assignment.driver?.user?.full_name || 'Unknown Driver'}
            </div>
            <div className="text-sm text-muted-foreground">
              {assignment.driver?.user?.email || '—'}
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <span className="text-muted-foreground uppercase text-sm">
          {assignment.driver?.employment_type || '—'}
        </span>
      </TableCell>
      <TableCell>
        <Badge variant={assignmentStatusVariant[assignment.status]}>
          {assignment.status}
        </Badge>
      </TableCell>
      <TableCell>
        <div className="text-sm">
          {new Date(assignment.requested_at).toLocaleDateString()}
        </div>
        <div className="text-xs text-muted-foreground">by {assignment.requested_by}</div>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={onView}>
            <Eye className="w-4 h-4" />
          </Button>
          {assignment.status === 'pending' && (
            <>
              <Button variant="ghost" size="sm" onClick={onDeny} disabled={isDenying}>
                <XCircle className="w-4 h-4 text-destructive" />
              </Button>
              <Button variant="ghost" size="sm" onClick={onApprove} disabled={isApproving}>
                <CheckCircle className="w-4 h-4 text-primary" />
              </Button>
            </>
          )}
          {assignment.status === 'assigned' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRemove}
              disabled={isRemoving}
            >
              <Trash2 className="w-4 h-4 text-destructive" />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

// Card component for driver assignments
function DriverAssignmentCard({
  assignment,
  onApprove,
  onDeny,
  onRemove,
  onView,
  isApproving,
  isDenying,
  isRemoving,
}: {
  assignment: DriverBrokerAssignment;
  onApprove: () => void;
  onDeny: () => void;
  onRemove: () => void;
  onView: () => void;
  isApproving: boolean;
  isDenying: boolean;
  isRemoving: boolean;
}) {
  const statusLabels: Record<AssignmentStatus, string> = {
    assigned: 'Assigned',
    pending: 'Pending',
    removed: 'Removed',
  };

  return (
    <Card className="h-full flex flex-col hover:shadow-soft transition-all">
      <CardContent className="p-4 space-y-3 flex-1 flex flex-col">
        {/* Header row with badge */}
        <div className="flex items-center justify-between">
          <Badge variant={assignmentStatusVariant[assignment.status]}>
            {statusLabels[assignment.status]}
          </Badge>
        </div>

        {/* Centered avatar and driver info */}
        <div className="flex flex-col items-center text-center cursor-pointer" onClick={onView}>
          {/* Avatar */}
          <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center mb-2">
            <span className="text-primary text-lg font-semibold">
              {assignment.driver?.user?.full_name?.charAt(0).toUpperCase() || '?'}
            </span>
          </div>

          {/* Driver Name */}
          <h3 className="font-semibold">{assignment.driver?.user?.full_name || 'Unknown Driver'}</h3>

          {/* Email */}
          <p className="text-sm text-muted-foreground">{assignment.driver?.user?.email || '—'}</p>
        </div>

        {/* Metadata Section */}
        <div className="border-t pt-3 space-y-2 text-sm">
          {/* Employment Type */}
          <div className="flex items-center gap-2 text-muted-foreground">
            <BriefcaseIcon className="h-4 w-4 shrink-0" />
            <span className="uppercase">{assignment.driver?.employment_type || '—'}</span>
          </div>

          {/* Requested Date */}
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="h-4 w-4 shrink-0" />
            <span>Requested {new Date(assignment.requested_at).toLocaleDateString()}</span>
          </div>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Actions */}
        {assignment.status === 'pending' ? (
          <div className="flex gap-2 mt-auto">
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={onDeny}
              disabled={isDenying}
            >
              Deny
            </Button>
            <Button 
              size="sm" 
              className="flex-1"
              onClick={onApprove}
              disabled={isApproving}
            >
              Approve
            </Button>
          </div>
        ) : (
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full mt-auto"
            onClick={onView}
          >
            <Eye className="h-4 w-4 mr-2" />
            View Details
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
